import type { ReadonlyDeep } from 'type-fest';

import { OrganizationInput, ThirdPartyTypeEnum } from '@e-socna/data-layer';
import { EMPTY_OBJECT } from '@e-socna/empty';
import { normalizeOrganizationIdentifierValue } from '@e-socna/third-parties';

import { pruneInput } from '../../../utils/prune-input.util';

import { addressForm2ApiMapper } from '../../Address/mappers/address.form2api.mapper';
import { isFormEmpty } from '../../utils/is-form-empty.util';
import { codingConfigForm2ApiMapper } from '../../CodingConfig/mappers/coding-config.form2api.mapper';

import type { OrganizationInput as IOrganizationInput } from '../hooks/useCreateOrganizationMutation.hook';
import type { ThirdPartyForm_Organization } from '../fragments/organization.fragment';

import { contactInfoForm2ApiMapper } from './contact-info.form2api.mapper';
import { bankInfoForm2ApiMapper } from './bank-info.form2api.mapper';
import { shareCapitalForm2ApiMapper } from './share-capital.form2api.mapper';

export function organizationForm2ApiMapper(
  organization: ReadonlyDeep<ThirdPartyForm_Organization>,
): ReadonlyDeep<IOrganizationInput> {
  const {
    bankInfo,
    billReceiverId,
    birthDate,
    color,
    contactInfo,
    legalStatusId,
    logoImageUri,
    motherId,
    nafId,
    shareCapital,
    vatNumber,
    ...organizationInput
  } = pruneInput(organization, OrganizationInput);

  const normalizedIdentifierValue = organization.identifier?.value
    ? normalizeOrganizationIdentifierValue(organization.identifier.value)
    : null;

  return {
    ...organizationInput,
    addresses: organization.addresses.map(addressForm2ApiMapper),
    bankInfo:
      !bankInfo || isFormEmpty({ node: bankInfo })
        ? null
        : bankInfoForm2ApiMapper(bankInfo),
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    billReceiverId: billReceiverId || null,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    birthDate: birthDate || null,
    ...(color ? { color } : EMPTY_OBJECT),
    codingConfig: codingConfigForm2ApiMapper(organization.codingConfig),
    identifier:
      normalizedIdentifierValue && organization.identifier
        ? {
            kind: organization.identifier.kind,
            value: normalizedIdentifierValue,
          }
        : null,
    contactInfo:
      !contactInfo || isFormEmpty({ node: contactInfo })
        ? null
        : contactInfoForm2ApiMapper(contactInfo),
    kind: ThirdPartyTypeEnum.Organization,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    logoImageUri: logoImageUri || null,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    vatNumber: vatNumber || null,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    legalStatusId: legalStatusId || null,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    motherId: motherId || null,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    nafId: nafId || null,
    shareCapital:
      !shareCapital || isFormEmpty({ node: shareCapital })
        ? null
        : shareCapitalForm2ApiMapper(shareCapital),
  };
}
